import { getRoofingMaterialExtension, getRoofingMaterialThickness } from "building-common/roofs/roofQuery";
import { mergeCoplanarFaceArrays, mergeCoplanarFaces } from "building-generation/faceMerge";
import { tagBuildingFaces } from "building-types/buildingGeomTag/buildingGeomTags";
import { type BuildingGeomCondition, type BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import { WallLateralDatumAnchor } from "building-types/datums/datumsTypesEnum";
import type { Porch } from "building-types/porches/porch";
import { DEFAULT_TRIM_PROFILE_DESCRIPTOR } from "building-types/profiles/defaultBoardTrimProfiles";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type {
  GeneratedRoof,
  GeneratedRoofSoffit,
  GeneratedRoofSystem,
} from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import type { RoofPerimeterRail, RoofRailConerInfo } from "building-types/roofs/roofPerimeterTypes";
import { RoofPerimeterRailType, RoofRailCornerType } from "building-types/roofs/roofPerimeterTypes";
import type {
  FasciaRail,
  FasciaTrimRail,
  FriezeBoardRail,
  FriezePlaneGroup,
  RoofTrimRail,
  SoffitRail,
} from "building-types/roofs/roofTrimTypes";
import {
  friezePlaneGroupNew,
  isRoofTrimRailTypeFrieze,
  ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
} from "building-types/roofs/roofTrimTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import { RoofSide } from "building-types/settings/roofTypes";
import { EaveCutType, EaveType, GableRoofType, WrapFriezeBoardMode } from "building-types/settings/settingsTypesEnum";
import type { WallSettings } from "building-types/settings/wallSettings";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import { RAFTER_DIST } from "common/constants/buildingObjects";
import { RAFTER_THICKNESS } from "common/constants/roofTrimConstants";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotNull, assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { getMaxDepthErrorHandle } from "doc/building/buildingGenerationIssuesTools";
import { isOpenEave } from "doc/fasciaTopTrimSizeToValue";
import type { MergedPropLibrary } from "doc/propTools";
import { DEFAULT_EAVE_EXTENSION_AT_COPLANAR_RAKE, DEFAULT_EAVE_RETURN_DISTANCE } from "doc/settings/defaultSettings";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { faceArrayViewEntityNew, baseDescriptorNew } from "doc/utils/viewEntityTools";
import { getExteriorWallOffset } from "doc/utils/wallTypeTools";
import { aabb2ByTwoPoints } from "geom/aabb2";
import { aabb2Height } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { aabb3ByTwoPoints } from "geom/aabb3";
import type { Face } from "geom/brep";
import { faceNew, tagFaces, faceIsDefined } from "geom/brep";
import { addConvexPlanesToBSPLeaf, isPointInsideBSP, isValidNodeBSP } from "geom/bsp";
import type { ClipFaceContext, SplitFaceBSPOptions } from "geom/bspClipping";
import { clipFaces, clipFacesWithPlane, clipFacesWithPlanes, clippingNodeNew, splitFacesBSP } from "geom/bspClipping";
import type { MaxBSPDepthErrorHandler, NodeBSP } from "geom/bspTypes";
import { ANGLE_EPSILON, DIST_EPSILON } from "geom/core";
import { extrudeFace, extrudeFaceAlongNormal, extrudeFaceAlongPolyLine3, extrusionToBRep } from "geom/extrude";
import { polyLine2PolyLine2DoIntersect } from "geom/intersect";
import { offsetPolyLine2 } from "geom/offset";
import {
  planeCopy,
  planeEval,
  planeIsCoplanar,
  planeMove,
  planeMoveAlongNormal,
  planeNew,
  planeNewXY,
  planeNormal,
  planeProject,
  planeProjectPolyLine3,
  planeReProjectPolyLine2,
  planeReverse,
  polyLine2OnPlane,
} from "geom/plane";
import { planeBSPFromPlane } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2AABB2,
  polyLine2Concat,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2EdgeNormal,
  polyLine2EdgeTangent,
  polyLine2End,
  polyLine2Extend,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2IsHorizontalEdge,
  polyLine2Move,
  polyLine2ReflectOverYAxis,
  polyLine2Reverse,
  polyLine2SetCW,
  polyLine2Simplify,
  polyLine2Start,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3Copy,
  polyLine3Edge,
  polyLine3EdgeCount,
  polyLine3EdgeSlopeInZ,
  polyLine3EdgeTangent,
  polyLine3EndPoint,
  polyLine3EvalEdge,
  polyLine3ExtendBothEnds,
  polyLine3ExtendEnd,
  polyLine3ExtendStart,
  polyLine3Length,
  polyLine3New,
  polyLine3OnPlane,
  polyLine3ReverseCopy,
  polyLine3StartPoint,
} from "geom/polyLine3";
import { rangeInclusiveByMinStep } from "geom/range";
import { vec2Add, vec2Dist, vec2Normalize } from "geom/vec2";
import { vec3AngleBetween, vec3Cross, vec3Normalize } from "geom/vec3";
import RBush from "rbush";
import type { BBox } from "rbush-3d";
import { RBush3D } from "rbush-3d";
import { pushMissingProfileReferenceIssue } from "../../elements/profileMissingReference";
import { getActiveProfileDescriptor, getProfileExtrusionDescriptorProfile } from "../../elements/profileTools";
import {
  boxEaveProfile,
  hasEaveSquaredCut,
  roofHasCornerAtPoint,
  squaredSlopedBoxEaveProfile,
} from "../../elements/roofTrimUtility";
import { gableDetailsAlongRail } from "../roofTrim";
import { extrudeRoofTopFaces, extrudeRoofingMaterialFaceEntities, extrudeRoofingMaterialFaces } from "./roofExtrusions";
import { mergeCoplanarRoofTopFaces } from "./roofFacesMerge";
import { collectOverhangClipping } from "./roofOverhangClippingVolumes";
import { getRoofPerimeterCornerInfo, getRoofPerimeterRailStartRafterDir, isRoofRailCorner } from "./roofPerimeterRails";
import { prepareRoofPerimeterRails } from "./roofPerimeterRailsPreparation";
import { buildSoffitDiscardBSP, buildSoffitFromRails } from "./roofSoffits";
import {
  buildExposedRaftersRails,
  buildFasciaRails,
  buildFasciaTopTrimRails,
  buildFriezeBoardRails,
  buildSoffitRails,
  getCollinearRoofTrimExtensions,
  getCollinearRoofTrimMiterPlanes,
  getIntersectingRoofTrimRailZLimit,
  getRoofTrimCornerInfo,
  isEaveRoofPerimeterRail,
  soffitCollinearExtensionFilter,
} from "./roofTrimRails";
import { buildEaveReturnPlaneGroups, buildRoofTopExtension, buildTrimPlaneGroups } from "./roofTrimsTools";

const CORNER_RAIL_EXTENSION = 100; // TODO: get this value from the corner angle
const SRC_RAIL_END_EXTENSION = 1;
const RAFTER_OVERHANG_THRESHOLD = 6;
const EXPOSED_RAFTER_RAIL_MIN_LENGTH = 0.5;
const BROAD_EAVE_RETURN_RTREE_BOX_HALF_WIDTH = 5.0;
const NARROW_EAVE_RETURN_RTREE_BOX_HALF_WIDTH = 1.0;
const ROOF_TRIM_COPLANAR_UV_ANGLE_TOLERANCE = 0.0001;

function getEaveReturnRTreeBoxSize({ eaveType }: RoofPerimeterRail): number {
  return eaveType === EaveType.BOXED_FULL_RETURN ||
    eaveType === EaveType.BOXED_MINI_HIP ||
    eaveType === EaveType.BOXED_MINI_SHED
    ? BROAD_EAVE_RETURN_RTREE_BOX_HALF_WIDTH
    : NARROW_EAVE_RETURN_RTREE_BOX_HALF_WIDTH;
}

function isVerticalRoof(subRoof: GeneratedRoof): boolean {
  return !polyLine2IsHorizontalEdge(planeProjectPolyLine3(planeNewXY(), subRoof.roofVolume.rails.peakRail), 0);
}

function isRoofRailFlatAtEnd(roofPerimeterRail: RoofPerimeterRail | undefined, isEnd: boolean): boolean {
  if (!roofPerimeterRail) {
    return false;
  }

  const { rail } = roofPerimeterRail;

  const slope = isEnd
    ? Math.abs(polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1))
    : Math.abs(polyLine3EdgeSlopeInZ(rail, 0));

  return slope < DIST_EPSILON;
}

interface HasEaveReturnOptions {
  readonly subRoof: GeneratedRoof;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly roofPerimeterRail: RoofPerimeterRail;
  readonly roofPerimeterRailsRTree: RBush3D;
  readonly isEnd: boolean;
}

interface HasEaveReturnResult {
  readonly cornerInfo: RoofRailConerInfo;
  readonly otherPerimeterRail?: RoofPerimeterRail;
}

interface EaveReturnBox extends BBox {
  readonly cornerType: RoofRailCornerType;
  readonly eaveOverhang: number;
}

function eaveReturnBoxNew(bbox: BBox, cornerType: RoofRailCornerType, eaveOverhang: number): EaveReturnBox {
  return {
    ...bbox,
    cornerType,
    eaveOverhang,
  };
}

function hasEaveReturn({
  subRoof,
  subRoofs,
  roofPerimeterRail,
  roofPerimeterRailsRTree,
  isEnd,
}: HasEaveReturnOptions): HasEaveReturnResult | false {
  const isFlat = isRoofRailFlatAtEnd(roofPerimeterRail, isEnd);

  if (isFlat) {
    return false;
  }

  const cornerInfo = getRoofPerimeterCornerInfo(roofPerimeterRail, roofPerimeterRailsRTree, isEnd, false);

  if (!cornerInfo.otherPerimeterRail) {
    return { cornerInfo, otherPerimeterRail: cornerInfo.otherPerimeterRail };
  }

  switch (cornerInfo.type) {
    case RoofRailCornerType.OUTSIDE:
      return { cornerInfo, otherPerimeterRail: cornerInfo.otherPerimeterRail };
    case RoofRailCornerType.COPLANAR:
      const { subRoofId: otherSubRoofId, edgeIndex: otherEdgeIndex } = cornerInfo.otherPerimeterRail;
      if (otherSubRoofId === subRoof.id) {
        return { cornerInfo, otherPerimeterRail: cornerInfo.otherPerimeterRail };
      }

      const { isStartEdge, isEndEdge } = roofPerimeterRail;
      if (!isStartEdge && !isEndEdge) {
        return { cornerInfo, otherPerimeterRail: cornerInfo.otherPerimeterRail };
      }

      // legacy behavior
      const otherSubRoof = subRoofs[otherSubRoofId];
      if (!otherSubRoof) {
        return false;
      }

      const { roofVolume: otherRoofVolume } = otherSubRoof;
      const { eaveEdges: otherEdges } = otherRoofVolume;
      const otherEdge = otherEdges[otherEdgeIndex];

      if (!otherEdge) {
        return { cornerInfo, otherPerimeterRail: cornerInfo.otherPerimeterRail };
      }

      const { startAlignedHostRoofId, endAlignedHostRoofId } = otherEdge;

      const hasNeighborRoof = isEndEdge ? !!endAlignedHostRoofId : !!startAlignedHostRoofId;
      if (hasNeighborRoof && !isVerticalRoof(subRoof)) {
        return false;
      }

      return { cornerInfo, otherPerimeterRail: cornerInfo.otherPerimeterRail };
    default:
      return false;
  }
}

interface BuildEaveReturnsRTreeOptions {
  readonly subRoof: GeneratedRoof;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly roofPerimeterRails: RoofPerimeterRail[];
  readonly roofPerimeterRailsRTree: RBush3D;
  readonly eaveReturnRTree: RBush3D;
  readonly wallSettings: WallSettings;
  readonly overlapBSP: NodeBSP | null;
}

function buildEaveReturnsRTree({
  subRoof,
  subRoofs,
  roofPerimeterRails,
  roofPerimeterRailsRTree,
  eaveReturnRTree,
  wallSettings,
  overlapBSP,
}: BuildEaveReturnsRTreeOptions): RBush<AABB2> {
  const { roofVolume, roofSettings } = subRoof;
  const { legacyEaveReturns } = roofVolume;
  const {
    eaveFasciaSize,
    eaveReturnDistance = DEFAULT_EAVE_RETURN_DISTANCE,
    eaveExtensionAtCoplanarRake = DEFAULT_EAVE_EXTENSION_AT_COPLANAR_RAKE,
    overhangDatum = WallLateralDatumAnchor.OUTSIDE_FACE_OF_FRAMING,
  } = roofSettings;
  const wallOffsetFromOverhangDatum = getExteriorWallOffset(wallSettings, overhangDatum);

  const gableReturnsRTree = new RBush<AABB2>();
  const addReturnRTreeEntry = (
    roofPerimeterRail: RoofPerimeterRail,
    cornerInfo: RoofRailConerInfo,
    eaveOverhang: number,
    isEnd: boolean,
  ) => {
    const { rail, eaveType, eaveCutType, edgePlane, isStartEdge, isEndEdge } = roofPerimeterRail;

    if (isOpenEave[eaveType]) {
      return;
    }

    if (legacyEaveReturns && !isStartEdge && !isEndEdge) {
      return;
    }

    const hasSquaredCut = hasEaveSquaredCut(eaveType, eaveCutType);
    const startSlope = polyLine3EdgeSlopeInZ(rail, 0);
    const endSlope = -polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1);

    const slope = isEnd ? endSlope : startSlope;
    const [railX, railY, railZ] = isEnd ? polyLine3EndPoint(rail) : polyLine3StartPoint(rail);
    const [nX, nY] = planeNormal(edgePlane);

    const hasCorner = roofHasCornerAtPoint({
      x: railX,
      y: railY,
      z: railZ,
      tx: nX,
      ty: nY,
      overlapBSP,
    });

    const eaveWidth = hasCorner
      ? eaveOverhang + eaveReturnDistance - wallOffsetFromOverhangDatum
      : eaveOverhang + eaveExtensionAtCoplanarRake - wallOffsetFromOverhangDatum;

    if (slope < DIST_EPSILON || eaveWidth < wallOffsetFromOverhangDatum) {
      return;
    }

    let profile = hasSquaredCut
      ? squaredSlopedBoxEaveProfile(eaveFasciaSize, slope, eaveWidth, 0, MULTIGABLE_BSP_EPSILON)
      : boxEaveProfile(eaveFasciaSize, slope, eaveWidth, 0, MULTIGABLE_BSP_EPSILON);

    if (isEnd) {
      const [edgeX, edgeY] = planeEval(edgePlane, 0, 0);
      const horizontalDistance = vec2Dist(railX, railY, edgeX, edgeY);

      profile = polyLine2ReflectOverYAxis(profile);
      profile = polyLine2Move(profile, horizontalDistance, 0);
    }

    const [u, v] = polyLine2Start(profile);
    const [x, y] = planeEval(edgePlane, u, v);

    const aabb2 = aabb2ByTwoPoints(
      x - ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
      y - ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
      x + ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
      y + ROOF_RAIL_RTREE_BOX_HALF_WIDTH,
    );

    const halfWidth = getEaveReturnRTreeBoxSize(roofPerimeterRail);
    const aabb3 = aabb3ByTwoPoints(
      railX - halfWidth,
      railY - halfWidth,
      railZ - halfWidth,
      railX + halfWidth,
      railY + halfWidth,
      railZ + halfWidth,
    );

    gableReturnsRTree.insert(aabb2);
    eaveReturnRTree.insert(eaveReturnBoxNew(aabb3, cornerInfo.type, eaveOverhang));
  };

  for (const roofPerimeterRail of roofPerimeterRails) {
    const eaveReturnAtStart = hasEaveReturn({
      subRoof,
      subRoofs,
      roofPerimeterRail,
      roofPerimeterRailsRTree,
      isEnd: false,
    });
    const eaveReturnAtEnd = hasEaveReturn({
      subRoof,
      subRoofs,
      roofPerimeterRail,
      roofPerimeterRailsRTree,
      isEnd: true,
    });

    if (eaveReturnAtStart) {
      const eaveOverhang =
        eaveReturnAtStart.cornerInfo.type === RoofRailCornerType.OUTSIDE
          ? eaveReturnAtStart.otherPerimeterRail?.overhang ?? roofPerimeterRail.roofEdgeStartOverhang
          : roofPerimeterRail.roofEdgeStartOverhang;

      addReturnRTreeEntry(roofPerimeterRail, eaveReturnAtStart.cornerInfo, eaveOverhang, false);
    }

    if (eaveReturnAtEnd) {
      const eaveOverhang =
        eaveReturnAtEnd.cornerInfo.type === RoofRailCornerType.OUTSIDE
          ? eaveReturnAtEnd.otherPerimeterRail?.overhang ?? roofPerimeterRail.roofEdgeEndOverhang
          : roofPerimeterRail.roofEdgeEndOverhang;

      addReturnRTreeEntry(roofPerimeterRail, eaveReturnAtEnd.cornerInfo, eaveOverhang, true);
    }
  }

  return gableReturnsRTree;
}

interface BuildEaveReturnsOptions {
  readonly subRoof: GeneratedRoof;
  readonly subRoofs: Table<GeneratedRoof>;
  readonly roofPerimeterRails: RoofPerimeterRail[];
  readonly eaveReturnRTree: RBush3D;
  readonly clipContext: MultiGableRoofClipContext;
  readonly fasciaBoards: FaceArrayViewEntity[];
  readonly fasciaTopTrims: FaceArrayViewEntity[];
  readonly friezeBoards: FaceArrayViewEntity[];
  readonly soffits: GeneratedRoofSoffit[];
  readonly gableVentFaces: FaceArrayViewEntity[];
  readonly roofTopFaces: FaceArrayViewEntity[];
  readonly props: Prop[];
  readonly walls: Table<WallSystem>;
  readonly wallOverlapForFriezeBoardBSPs: NodeBSP[];
  readonly wallSettings: WallSettings;
  readonly profileSource: ActiveProfileSource;
  readonly propSettings: PropAttributeTable;
  readonly mergedPropLibrary: MergedPropLibrary;
  readonly issues: BuildingGenerationIssue[];
}

const TAG_TABLE_TRIM: BuildingGeomCondition = {
  type: "CONDITION",
  condition: "TABLE_TRIM",
};

const TAG_TOP_TRIM: BuildingGeomCondition = {
  type: "CONDITION",
  condition: "FASCIA_TOP_TRIM",
};

const TAG_FASCIA_BOARD: BuildingGeomCondition = {
  type: "CONDITION",
  condition: "FASCIA_BOARD",
};

async function buildEaveReturns({
  subRoof,
  subRoofs,
  roofPerimeterRails,
  eaveReturnRTree,
  clipContext,
  fasciaBoards,
  fasciaTopTrims,
  friezeBoards,
  soffits,
  roofTopFaces,
  gableVentFaces,
  props,
  walls,
  wallOverlapForFriezeBoardBSPs,
  wallSettings,
  profileSource,
  propSettings,
  mergedPropLibrary,
  issues,
}: BuildEaveReturnsOptions): Promise<void> {
  const { roofVolume } = subRoof;
  const { exactEdges } = roofVolume;
  const { insideBSP, discardBSP, overlapBSP } = clipContext;

  const hasEaveReturnAtRail = (roofPerimeterRail: RoofPerimeterRail, isEnd: boolean): EaveReturnBox | undefined => {
    const { baseRail } = roofPerimeterRail;
    const [baseX, baseY, baseZ] = isEnd ? polyLine3EndPoint(baseRail) : polyLine3StartPoint(baseRail);
    const halfWidth = getEaveReturnRTreeBoxSize(roofPerimeterRail);
    const baseAABB = aabb3ByTwoPoints(
      baseX - halfWidth,
      baseY - halfWidth,
      baseZ - halfWidth,
      baseX + halfWidth,
      baseY + halfWidth,
      baseZ + halfWidth,
    );

    const baseResults = eaveReturnRTree.search(baseAABB);
    if (baseResults.length > 0) {
      return baseResults[0] as EaveReturnBox;
    }

    return undefined;
  };

  const processedEdges: { [key: number]: boolean } = {};

  for (const roofPerimeterRail of roofPerimeterRails) {
    const { edgeIndex } = roofPerimeterRail;

    if (processedEdges[edgeIndex]) {
      continue;
    }

    processedEdges[edgeIndex] = true;

    const isFlatAtStart = isRoofRailFlatAtEnd(roofPerimeterRail, false);
    const isFlatAtEnd = isRoofRailFlatAtEnd(roofPerimeterRail, true);

    if (isFlatAtStart && isFlatAtEnd) {
      continue;
    }

    const { baseRail, overhang, baseEdge, exteriorWallOffsetWithFinish, friezeBoard, isEndEdge } = roofPerimeterRail;

    const { roofMaterialTrimAndProps } = exactEdges[edgeIndex];

    let addStartDetails = false;
    let addEndDetails = false;
    let startEaveOverhang = roofPerimeterRail.roofEdgeStartOverhang;
    let endEaveOverhang = roofPerimeterRail.roofEdgeEndOverhang;

    const eaveReturnAtEnd = hasEaveReturnAtRail(roofPerimeterRail, true);
    const eaveReturnAtStart = hasEaveReturnAtRail(roofPerimeterRail, false);

    if (eaveReturnAtStart) {
      startEaveOverhang =
        eaveReturnAtStart.cornerType === RoofRailCornerType.OUTSIDE
          ? eaveReturnAtStart.eaveOverhang
          : roofPerimeterRail.roofEdgeStartOverhang;
      addStartDetails = true;
    }

    if (eaveReturnAtEnd) {
      endEaveOverhang =
        eaveReturnAtEnd.cornerType === RoofRailCornerType.OUTSIDE
          ? eaveReturnAtEnd.eaveOverhang
          : roofPerimeterRail.roofEdgeEndOverhang;
      addEndDetails = true;
    }

    const returnFasciaBoards: FaceArrayViewEntity[] = [];
    const returnFasciaTopTrims: FaceArrayViewEntity[] = [];
    const returnFriezeBoards: FaceArrayViewEntity[] = [];
    const returnSoffits: GeneratedRoofSoffit[] = [];
    const gableEndVentFaces: FaceArrayViewEntity[] = [];

    await gableDetailsAlongRail({
      subRoof,
      subRoofs,
      walls,
      rail: polyLine3ReverseCopy(baseRail),
      roofPerimeterRail,
      edge: baseEdge,
      exteriorWallOffsetWithFinish,
      endRoofOverhang: overhang,
      startEaveOverhang: endEaveOverhang,
      endEaveOverhang: startEaveOverhang,
      addStartDetails: addEndDetails,
      addEndDetails: addStartDetails,
      fasciaBoards: returnFasciaBoards,
      fasciaTopTrims: returnFasciaTopTrims,
      friezeBoards: returnFriezeBoards,
      soffits: returnSoffits,
      gableVentFaces: gableEndVentFaces,
      roofTopFaces,
      props,
      wallSettings,
      profileSource,
      propSettings,
      mergedPropLibrary,
      includeFriezeBoards: (friezeBoard?.length ?? 0) > 0, // TODO: do we need to implement stacked frieze boards for the gable returns?
      discardBSP,
      overlapBSP,
      insideBSP,
      wallOverlapForFriezeBoardBSPs,
      isEndSide: isEndEdge,
      roofMaterialTrimAndProps,
      issues,
    });

    const tags: BuildingGeomTag[] = [
      {
        type: "PARENT",
        parentId: subRoof.id,
        parentType: subRoof.roofType,
      },
      TAG_TABLE_TRIM,
    ];

    const taggedSoffits = tagTrimFaceArrays(returnSoffits, tags);
    for (const soffit of taggedSoffits) {
      const soffitEntityParentTag: BuildingGeomTag = {
        type: "PARENT",
        parentType: "ROOF_SOFFIT",
        parentId: soffit.id,
      };
      const taggedFaces = tagBuildingFaces(soffit.faces, [
        { type: "CONDITION", condition: "SOFFIT_FACE" },
        soffitEntityParentTag,
      ]);
      soffits.push({ ...soffit, faces: taggedFaces });
    }

    fasciaBoards.push(...tagTrimFaceArrays(returnFasciaBoards, tags));
    fasciaTopTrims.push(...tagTrimFaceArrays(returnFasciaTopTrims, tags));
    friezeBoards.push(...tagTrimFaceArrays(returnFriezeBoards, tags));
    gableVentFaces.push(...tagTrimFaceArrays(gableEndVentFaces, tags));
  }
}

interface BuildFasciaBoardOptions {
  readonly subRoof: GeneratedRoof;
  readonly roofPerimeterRail: RoofPerimeterRail;
  readonly topRailNoExtension: PolyLine2;
  readonly topRail: PolyLine2;
  readonly bottomRail: PolyLine2;
  readonly plane: Plane;
  readonly boardSize: number;
  readonly prevBoardSize: number;
  readonly nextBoardSize: number;
  readonly boardThickness: number;
  readonly entityType: ViewEntityType;
  readonly startClipPlane?: Plane;
  readonly endClipPlane?: Plane;
  readonly eaveReturnPlaneGroups: Plane[][];
  readonly fasciaBoards?: FaceArrayViewEntity[];
  readonly dirX: number;
  readonly dirY: number;
  readonly dirZ: number;
  readonly maxDepthErrorHandle: MaxBSPDepthErrorHandler;
}

function buildFasciaBoard({
  subRoof,
  roofPerimeterRail,
  topRailNoExtension,
  topRail,
  bottomRail,
  plane,
  boardSize,
  prevBoardSize,
  nextBoardSize,
  boardThickness,
  entityType,
  startClipPlane,
  endClipPlane,
  eaveReturnPlaneGroups,
  dirX,
  dirY,
  dirZ,
  fasciaBoards = [],
  maxDepthErrorHandle,
}: BuildFasciaBoardOptions): FaceArrayViewEntity[] {
  const { type, rail, fasciaMaterial, fasciaTopTrimMaterial, eaveType, eaveCutType, overhang } = roofPerimeterRail;

  const {
    roofSettings: { eaveReturnOverhang = overhang },
  } = subRoof;

  //the following special solution is offseting top rail to avoid z-fighting with roof top faces
  let topRailOffseted = polyLine2Copy(topRail);
  if (type === RoofPerimeterRailType.RAKE && !isOpenEave[eaveType] && eaveReturnOverhang > overhang) {
    topRailOffseted = offsetPolyLine2(topRail, MULTIGABLE_BSP_EPSILON);
  }

  if (!(polyLine2EdgeCount(topRailOffseted) === polyLine2EdgeCount(bottomRail))) {
    console.assert(false);
  }

  // build the profiles and faces to extrude
  const [normalX, normalY, normalZ] = planeNormal(plane);
  const faces: Face[] = [];
  for (let i = 0, l = polyLine2EdgeCount(topRailOffseted); i < l; i++) {
    // get the top edge
    const topEdge = polyLine2Edge(topRailOffseted, i);

    // skip if the edge is too short
    if (polyLine2EdgeLength(topEdge, 0) < DIST_EPSILON) {
      continue;
    }

    // get the bottom edge
    const bottomEdge = polyLine2Reverse(polyLine2Edge(bottomRail, i));

    // build the face plane to orient the face UVs
    const topEdge3D = polyLine2OnPlane(plane, topEdge);
    const [originX, originY, originZ] = polyLine3StartPoint(topEdge3D);
    const [uX, uY, uZ] = polyLine3EdgeTangent(topEdge3D, 0);
    const [vX, vY, vZ] = vec3Cross(normalX, normalY, normalZ, uX, uY, uZ);

    const facePlane = planeNew(originX, originY, originZ, uX, uY, uZ, vX, vY, vZ);

    // build the profile relative to the main plane
    const srcProfile = polyLine2SetCW(polyLine2Concat(topEdge, bottomEdge, true));

    // reproject the profile to the face plane
    const profile = planeReProjectPolyLine2(srcProfile, plane, facePlane);

    // build the face
    const face = faceNew([profile], facePlane);

    faces.push(face);
  }

  const tag = entityType === ViewEntityType.FASCIA_TRIM ? TAG_TOP_TRIM : TAG_FASCIA_BOARD;

  // clip the faces
  const planeGroups: Plane[][] = [...eaveReturnPlaneGroups];
  if (
    type === RoofPerimeterRailType.RAKE &&
    Math.abs(boardSize - prevBoardSize) < DIST_EPSILON &&
    Math.abs(boardSize - nextBoardSize) < DIST_EPSILON
  ) {
    if (!rail) {
      return fasciaBoards;
    }

    const hasSquareCutEave: boolean = hasEaveSquaredCut(eaveType, eaveCutType);
    // get the rail slopes
    const signedStartSlope = polyLine3EdgeSlopeInZ(rail, 0);
    const signedEndSlope = -polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1);
    planeGroups.push(
      ...buildTrimPlaneGroups(
        topRailNoExtension,
        plane,
        prevBoardSize,
        nextBoardSize,
        signedStartSlope,
        signedEndSlope,
        hasSquareCutEave,
      ),
    );
  }

  let clippedFaces = faces;
  if (planeGroups.length > 0) {
    const bsps = planeGroups
      .map((planes) => addConvexPlanesToBSPLeaf(planes.map((x) => planeBSPFromPlane(x))))
      .filter(isValidNodeBSP);

    clippedFaces = clipFaces({
      faces,
      options: {
        clippingNodes: bsps.map((bsp) => clippingNodeNew(bsp)),
      },
    });
  }

  // Later face merging done at clipAndMergeRoofTrims only merges coplanar faces from the same face array.
  // The following merge is avoiding having different face arrays for the same fascia segments,
  // as they won't be merged together at clipAndMergeRoofTrims.
  const mergedFaces =
    clippedFaces.length > 1
      ? mergeCoplanarFaces({
          inputFaces: clippedFaces,
          coplanarUvAngleTolerance: ROOF_TRIM_COPLANAR_UV_ANGLE_TOLERANCE,
        })
      : clippedFaces;

  // prepare corner clipping planes
  const clipPlanes: Plane[] = [];
  if (startClipPlane) {
    clipPlanes.push(startClipPlane);
  }

  if (endClipPlane) {
    clipPlanes.push(endClipPlane);
  }

  const material = entityType === ViewEntityType.FASCIA_TRIM ? fasciaTopTrimMaterial : fasciaMaterial;

  const extrusionDirX = -dirX * boardThickness;
  const extrusionDirY = -dirY * boardThickness;
  const extrusionDirZ = -dirZ * boardThickness;

  // generate the geometry with the clipped and merged faces
  for (const mergedFace of mergedFaces) {
    // extrude the face
    const extrusion = extrudeFace(mergedFace, extrusionDirX, extrusionDirY, extrusionDirZ);
    const brep = extrusionToBRep(extrusion);

    // corner clipping
    const faces = tagFaces(
      clipFacesWithPlanes({
        faces: brep.faces,
        planes: clipPlanes,
        addCapFaces: true,
        maxDepthErrorHandle,
      }).filter(faceIsDefined),
      tag,
    );

    // add the result
    fasciaBoards.push(faceArrayViewEntityNew(faces, baseDescriptorNew(entityType), material));
  }

  return fasciaBoards;
}

interface BuildExposedRaftersOptions {
  readonly subRoof: GeneratedRoof;
  readonly exposedRaftersRail: SoffitRail;
  readonly startClipPlane?: Plane;
  readonly endClipPlane?: Plane;
  readonly exposedRafters?: FaceArrayViewEntity[];
  readonly maxDepthErrorHandle: MaxBSPDepthErrorHandler;
}

const tagRoofRafter = { type: "CONDITION", condition: "ROOF_RAFTER" };

function buildExposedRafters({
  subRoof,
  exposedRaftersRail,
  exposedRafters = [],
  startClipPlane = undefined,
  endClipPlane = undefined,
  maxDepthErrorHandle,
}: BuildExposedRaftersOptions): FaceArrayViewEntity[] {
  const {
    innerPlane,
    midPlane,
    outerPlane,
    fasciaTopRail,
    outerTopRail,
    outerBottomRail,
    midBottomRail,
    innerTopRail,
    innerBottomRail,
    material,
    roofPerimeterRail,
  } = exposedRaftersRail;

  const { type, prevPerimeterRail, nextPerimeterRail, fasciaBoardThickness, side } = roofPerimeterRail;

  const {
    roofSettings: { lookoutRafters = true },
  } = subRoof;

  if (!lookoutRafters && type === RoofPerimeterRailType.RAKE) {
    return exposedRafters;
  }

  const generateRaftersBeyondBaseEdge = type !== RoofPerimeterRailType.RAKE && !lookoutRafters;

  const {
    overhang: startOverhang = 0,
    exteriorWallOffsetWithSheathing: startExteriorWallOffsetWithSheating = 0,
    type: prevType,
    rail: prevRail,
  } = prevPerimeterRail ?? {};

  const {
    overhang: endOverhang = 0,
    exteriorWallOffsetWithSheathing: endExteriorWallOffsetWithSheathing = 0,
    type: nextType,
    rail: nextRail,
  } = nextPerimeterRail ?? {};

  if (!(fasciaTopRail.length === outerBottomRail.length)) {
    console.assert(false);
  }
  if (!(fasciaTopRail.length === outerTopRail.length)) {
    console.assert(false);
  }
  if (!(fasciaTopRail.length === innerBottomRail.length)) {
    console.assert(false);
  }
  if (!(fasciaTopRail.length === innerTopRail.length)) {
    console.assert(false);
  }
  if (!(fasciaTopRail.length === midBottomRail.length)) {
    console.assert(false);
  }

  const outerPlaneShifted = planeMoveAlongNormal(planeCopy(outerPlane), fasciaBoardThickness); //to make it flush with the fascia board
  const outerBottomRail3D: PolyLine3 = polyLine2OnPlane(outerPlaneShifted, outerBottomRail);
  const outerTopRail3D: PolyLine3 = polyLine2OnPlane(outerPlaneShifted, outerTopRail);
  const innerBottomRail3D: PolyLine3 = polyLine2OnPlane(innerPlane, innerBottomRail);
  const innerTopRail3D: PolyLine3 = polyLine2OnPlane(innerPlane, innerTopRail);
  const midBottomRail3D: PolyLine3 = polyLine2OnPlane(midPlane, midBottomRail);

  let dx = 0,
    dy = 0,
    dz = 0;
  if (type !== RoofPerimeterRailType.RAKE && generateRaftersBeyondBaseEdge) {
    if (prevType === RoofPerimeterRailType.RAKE && side === RoofSide.LEFT && prevRail) {
      const edge1 = polyLine3Edge(prevRail, polyLine3EdgeCount(prevRail) - 1);
      dx = edge1[0] - edge1[3];
      dy = edge1[1] - edge1[4];
      dz = edge1[2] - edge1[5];
    }
    if (nextType === RoofPerimeterRailType.RAKE && side === RoofSide.RIGHT && nextRail) {
      const edge1 = polyLine3Edge(nextRail, 0);
      dx = edge1[3] - edge1[0];
      dy = edge1[4] - edge1[1];
      dz = edge1[5] - edge1[2];
    }
  }
  let innerBottomRail3DShifted: PolyLine3 | null = null;
  let innerTopRail3DShifted: PolyLine3 | null = null;
  let createAdditionalProfile: boolean = false;
  if (dx !== 0 || dy !== 0 || dz !== 0) {
    const innerPlaneShifted = planeMove(planeCopy(innerPlane), dx, dy, dz);
    innerBottomRail3DShifted = polyLine2OnPlane(innerPlaneShifted, innerBottomRail);
    innerTopRail3DShifted = polyLine2OnPlane(innerPlaneShifted, innerTopRail);
    createAdditionalProfile = true;
  }

  // prepare corner clipping planes
  const clipPlanes: Plane[] = [];
  if (startClipPlane) {
    clipPlanes.push(startClipPlane);
  }

  if (endClipPlane) {
    clipPlanes.push(endClipPlane);
  }

  const rafterThickness = RAFTER_THICKNESS;
  let outerTopRailReducedByOverhang: PolyLine2 | undefined;
  let rafterRail: PolyLine3 | undefined;
  let rafterRailOverhangAtStart: PolyLine3 | null | undefined;
  let rafterRailOverhangAtEnd: PolyLine3 | null | undefined;
  if (type !== RoofPerimeterRailType.RAKE) {
    const startOffset = startOverhang - startExteriorWallOffsetWithSheating,
      endOffset = endOverhang - endExteriorWallOffsetWithSheathing - rafterThickness;
    if (generateRaftersBeyondBaseEdge) {
      //in this case we only insert new points in the outer top rail
      const numberOfEdges = polyLine2EdgeCount(outerTopRail);
      const [startX, startY] = polyLine2Start(outerTopRail);
      const [startDirX, startDirY] = polyLine2EdgeTangent(outerTopRail, 0);
      const [endX, endY] = polyLine2End(outerTopRail);
      const [endDirX, endDirY] = polyLine2EdgeTangent(outerTopRail, numberOfEdges - 1, true);
      const [secondX, secondY] = vec2Add(startX, startY, startDirX * startOffset, startDirY * startOffset);
      const [secondLastX, secondLastY] = vec2Add(
        endX,
        endY,
        endDirX * (endOffset + rafterThickness),
        endDirY * (endOffset + rafterThickness),
      );
      rafterRailOverhangAtStart = polyLine2OnPlane(outerPlaneShifted, polyLine2New([startX, startY, secondX, secondY]));
      rafterRailOverhangAtEnd = polyLine2OnPlane(
        outerPlaneShifted,
        polyLine2New([endX, endY, secondLastX, secondLastY]),
      );
    }
    outerTopRailReducedByOverhang = polyLine2Extend(outerTopRail, -startOffset, -endOffset);
    rafterRail = polyLine2OnPlane(outerPlaneShifted, outerTopRailReducedByOverhang);
  } else {
    rafterRail = polyLine2OnPlane(outerPlaneShifted, outerTopRail);
    const slopeBeg = polyLine3EdgeSlopeInZ(rafterRail, 0);
    const startOverhangReduction =
      -(startOverhang - startExteriorWallOffsetWithSheating) * Math.sqrt(1 + slopeBeg * slopeBeg);
    const slopeEnd = polyLine3EdgeSlopeInZ(rafterRail, polyLine3EdgeCount(rafterRail) - 1);
    const endOverhangReduction =
      -(endOverhang - endExteriorWallOffsetWithSheathing) * Math.sqrt(1 + slopeEnd * slopeEnd);
    rafterRail = polyLine3ExtendBothEnds(rafterRail, startOverhangReduction, endOverhangReduction);
  }

  // TODO: build exposed rafters
  const [normX, normY, normZ] = planeNormal(outerPlane);

  // build the profile and the face to extrude
  const buildRafters = (
    edgeRail: PolyLine3,
    rafterProfile3D: PolyLine3,
    clippingPlaneFaces: Plane[],
    skipFirst?: boolean,
    skipLast?: boolean,
  ) => {
    const [tangentX, tangentY, tangentZ] = polyLine3EdgeTangent(edgeRail, 0);
    const [vX, vY, vZ] = vec3Cross(tangentX, tangentY, tangentZ, normX, normY, normZ);

    const edgeRailLength = polyLine3Length(edgeRail);

    if (edgeRailLength < EXPOSED_RAFTER_RAIL_MIN_LENGTH) {
      return;
    }

    const paramsAbsolute = rangeInclusiveByMinStep(0, edgeRailLength, RAFTER_DIST);
    const params = paramsAbsolute.map((x) => x / edgeRailLength);
    const paramsLength = params.length;
    let index = 0;

    for (const param of params) {
      index++;
      if ((index === 1 && skipFirst) || (index === paramsLength && skipLast)) {
        continue;
      }
      const [x, y, z] = polyLine3EvalEdge(edgeRail, 0, param);
      const rafterPlane = planeNew(x, y, z, vX, vY, vZ, normX, normY, normZ);
      const rafterProfile2D = planeProjectPolyLine3(rafterPlane, rafterProfile3D);
      polyLine2SetCW(rafterProfile2D);
      const face = faceNew([rafterProfile2D], rafterPlane);

      // extrude the face
      const extrusion = extrudeFaceAlongNormal(face, rafterThickness);
      const brep = extrusionToBRep(extrusion);

      // corner clipping
      const faces = tagFaces(
        clipFacesWithPlanes({
          faces: brep.faces,
          planes: clippingPlaneFaces,
          addCapFaces: true,
          maxDepthErrorHandle,
        }).filter(faceIsDefined),
        tagRoofRafter,
      );

      // add the result
      exposedRafters.push(faceArrayViewEntityNew(faces, baseDescriptorNew(ViewEntityType.RAFTER_EXTENSION), material));
    }
  };

  const edgeCount = polyLine3EdgeCount(rafterRail);
  let profileEndingWallPlane: number[];

  for (let i = 0; i < edgeCount; i++) {
    profileEndingWallPlane = [
      outerBottomRail3D[0 + i * 3],
      outerBottomRail3D[1 + i * 3],
      outerBottomRail3D[2 + i * 3],
      outerTopRail3D[0 + i * 3],
      outerTopRail3D[1 + i * 3],
      outerTopRail3D[2 + i * 3],
      innerTopRail3D[0 + i * 3],
      innerTopRail3D[1 + i * 3],
      innerTopRail3D[2 + i * 3],
      innerBottomRail3D[0 + i * 3],
      innerBottomRail3D[1 + i * 3],
      innerBottomRail3D[2 + i * 3],
    ];

    const hasValidMidRail = !planeIsCoplanar(midPlane, outerPlane);
    if (hasValidMidRail) {
      profileEndingWallPlane.push(midBottomRail3D[0 + i * 3], midBottomRail3D[1 + i * 3], midBottomRail3D[2 + i * 3]);
    }
    profileEndingWallPlane.push(
      outerBottomRail3D[0 + i * 3],
      outerBottomRail3D[1 + i * 3],
      outerBottomRail3D[2 + i * 3],
    );

    const edgeRail = polyLine3Edge(rafterRail, i);
    const rafterProfile3D = polyLine3New([...profileEndingWallPlane]);

    buildRafters(edgeRail, rafterProfile3D, clipPlanes);
  }

  let profileEndingRakeMidPlane: number[];

  if (createAdditionalProfile) {
    assertIsNotNull(innerTopRail3DShifted);
    assertIsNotNull(innerBottomRail3DShifted);

    profileEndingRakeMidPlane = [
      outerBottomRail3D[0],
      outerBottomRail3D[1],
      outerBottomRail3D[2],
      outerTopRail3D[0],
      outerTopRail3D[1],
      outerTopRail3D[2],
      innerTopRail3DShifted[0],
      innerTopRail3DShifted[1],
      innerTopRail3DShifted[2],
      innerBottomRail3DShifted[0],
      innerBottomRail3DShifted[1],
      innerBottomRail3DShifted[2],
    ];

    const hasValidMidRail = !planeIsCoplanar(midPlane, outerPlane);
    if (hasValidMidRail) {
      profileEndingRakeMidPlane.push(midBottomRail3D[0], midBottomRail3D[1], midBottomRail3D[2]);
    }
    profileEndingRakeMidPlane.push(outerBottomRail3D[0], outerBottomRail3D[1], outerBottomRail3D[2]);

    const rafterProfile3D = polyLine3New([...profileEndingRakeMidPlane]);

    assertIsNotNullOrUndefined(rafterRailOverhangAtStart);
    const lengthOverhangStart = polyLine3Length(rafterRailOverhangAtStart);

    if (rafterRailOverhangAtStart && lengthOverhangStart > RAFTER_OVERHANG_THRESHOLD) {
      buildRafters(rafterRailOverhangAtStart, rafterProfile3D, []);
    }

    assertIsNotNullOrUndefined(rafterRailOverhangAtEnd);
    const lengthOverhangEnd = polyLine3Length(rafterRailOverhangAtEnd);

    if (rafterRailOverhangAtEnd && lengthOverhangEnd > RAFTER_OVERHANG_THRESHOLD) {
      buildRafters(rafterRailOverhangAtEnd, rafterProfile3D, []);
    }
  }

  return exposedRafters;
}

interface BuildExposedRaftersFromRailsOptions {
  readonly subRoof: GeneratedRoof;
  readonly exposedRaftersRails: SoffitRail[];
  readonly exposedRaftersRTree: RBush3D;
  readonly exposedRafters?: FaceArrayViewEntity[];
  readonly extensionRoofTopFaces?: RoofFace[];
  readonly maxDepthErrorHandle: MaxBSPDepthErrorHandler;
}

function buildExposedRaftersFromRails({
  subRoof,
  exposedRaftersRails,
  exposedRaftersRTree,
  exposedRafters = [],
  extensionRoofTopFaces = [],
  maxDepthErrorHandle,
}: BuildExposedRaftersFromRailsOptions): FaceArrayViewEntity[] {
  for (const exposedRaftersRail of exposedRaftersRails) {
    const { roofPerimeterRail } = exposedRaftersRail;
    const { rail, baseEdge, overhang, exteriorWallOffsetWithSheathing } = roofPerimeterRail;

    if (overhang - exteriorWallOffsetWithSheathing < DIST_EPSILON) {
      continue;
    }

    if (!rail) {
      continue;
    }

    // get the rail slopes
    const railStartSlope = Math.abs(polyLine3EdgeSlopeInZ(rail, 0));
    const railEndSlope = Math.abs(polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1));

    // corner types
    const {
      type: startCornerType,
      miterPlane: startMiterPlane,
      angle: startCornerAngle,
    } = getRoofTrimCornerInfo(exposedRaftersRail, exposedRaftersRTree, false, true);

    const {
      type: endCornerType,
      miterPlane: endMiterPlane,
      angle: endCornerAngle,
    } = getRoofTrimCornerInfo(exposedRaftersRail, exposedRaftersRTree, true, true);

    // prepare clip planes
    let startClipPlane: Plane | undefined = undefined;
    let endClipPlane: Plane | undefined = undefined;

    if (
      Math.abs(railStartSlope) < DIST_EPSILON &&
      roofPerimeterRail?.prevPerimeterRail?.type === RoofPerimeterRailType.RAKE &&
      startCornerType === RoofRailCornerType.OUTSIDE
    ) {
      if (startCornerAngle >= Math.PI * 0.5 - ANGLE_EPSILON) {
        const [x, y] = polyLine2Start(baseEdge);
        const [uX, uY] = polyLine2EdgeNormal(baseEdge, 0);
        startClipPlane = planeMoveAlongNormal(planeNew(x, y, 0, uX, uY, 0, 0, 0, 1), exteriorWallOffsetWithSheathing);
      } else {
        startClipPlane = startMiterPlane;
      }
    }

    if (
      Math.abs(railEndSlope) < DIST_EPSILON &&
      roofPerimeterRail?.nextPerimeterRail?.type === RoofPerimeterRailType.RAKE &&
      endCornerType === RoofRailCornerType.OUTSIDE
    ) {
      if (endCornerAngle >= Math.PI * 0.5 - ANGLE_EPSILON) {
        const [x, y] = polyLine2End(baseEdge);
        const [uX, uY] = polyLine2EdgeNormal(baseEdge, polyLine2EdgeCount(baseEdge) - 1, true);

        endClipPlane = planeMoveAlongNormal(planeNew(x, y, 0, uX, uY, 0, 0, 0, 1), exteriorWallOffsetWithSheathing);
      } else {
        endClipPlane = endMiterPlane;
      }
    }

    // build the exposed eave and rake rafters
    buildExposedRafters({
      subRoof,
      exposedRaftersRail,
      startClipPlane,
      endClipPlane,
      exposedRafters,
      maxDepthErrorHandle,
    });

    // build roof top extension
    buildRoofTopExtension({
      subRoof,
      soffitRail: exposedRaftersRail,
      isEnd: false,
      extensionRoofTopFaces,
    });

    buildRoofTopExtension({
      subRoof,
      soffitRail: exposedRaftersRail,
      isEnd: true,
      extensionRoofTopFaces,
    });
  }

  return exposedRafters;
}

interface ClipAndMergeRoofTrimsOptions {
  readonly subRoof: GeneratedRoof;
  readonly exposedRafters: FaceArrayViewEntity[];
  readonly fasciaBoards: FaceArrayViewEntity[];
  readonly fasciaTopTrims: FaceArrayViewEntity[];
  readonly friezeBoards: FaceArrayViewEntity[];
  readonly soffits: GeneratedRoofSoffit[];
  readonly discardSoffitBSP: NodeBSP | null;
  readonly clipContext: MultiGableRoofClipContext;
}

function clipAndMergeRoofTrims({
  subRoof,
  exposedRafters,
  fasciaBoards,
  fasciaTopTrims,
  friezeBoards,
  soffits,
  discardSoffitBSP,
  clipContext,
}: ClipAndMergeRoofTrimsOptions): GeneratedRoof {
  // clipping
  const clippedExposedRafters = clipFacesEntities({
    entities: exposedRafters,
    discardSoffitBSP,
    clipContext,
  });

  const clippedFriezeBoards = clipFacesEntities({
    entities: friezeBoards,
    discardSoffitBSP,
    clipContext,
  });

  const clippedSoffits = clipFacesEntities({
    entities: Object.values(soffits),
    discardSoffitBSP,
    clipContext,
  });

  const clippedFasciaBoards = clipFacesEntities({
    entities: fasciaBoards,
    discardSoffitBSP,
    clipContext,
  });

  const clippedFasciaTopTrims = clipFacesEntities({
    entities: fasciaTopTrims,
    discardSoffitBSP,
    clipContext,
  });

  const newSoffits: RWTable<GeneratedRoofSoffit> = {};
  Object.assign(newSoffits, subRoof.soffits);
  Object.assign(newSoffits, clippedSoffits);

  // merge coplanar faces while keeping the plane orientation
  return {
    ...subRoof,
    exposedRafters: mergeCoplanarFaceArrays({
      faceArrays: [...subRoof.exposedRafters, ...clippedExposedRafters],
      coplanarUvAngleTolerance: ROOF_TRIM_COPLANAR_UV_ANGLE_TOLERANCE,
    }),
    fasciaBoards: mergeCoplanarFaceArrays({
      faceArrays: [...subRoof.fasciaBoards, ...clippedFasciaBoards],
      coplanarUvAngleTolerance: ROOF_TRIM_COPLANAR_UV_ANGLE_TOLERANCE,
    }),
    fasciaTopTrims: mergeCoplanarFaceArrays({
      faceArrays: [...subRoof.fasciaTopTrims, ...clippedFasciaTopTrims],
      coplanarUvAngleTolerance: ROOF_TRIM_COPLANAR_UV_ANGLE_TOLERANCE,
    }),
    friezeBoards: mergeCoplanarFaceArrays({
      faceArrays: [...subRoof.friezeBoards, ...clippedFriezeBoards],
      coplanarUvAngleTolerance: ROOF_TRIM_COPLANAR_UV_ANGLE_TOLERANCE,
    }),
    soffits: mergeCoplanarFaceArrays<GeneratedRoofSoffit>({
      faceArrays: [...subRoof.soffits, ...clippedSoffits],
      coplanarUvAngleTolerance: ROOF_TRIM_COPLANAR_UV_ANGLE_TOLERANCE,
    }),
  };
}

interface ClipFacesEntitiesOptions<T extends FaceArrayViewEntity> {
  readonly entities: T[];
  readonly discardSoffitBSP: NodeBSP | null;
  readonly clipContext: MultiGableRoofClipContext;
  readonly clippedEntities?: T[];
}

function clipFacesEntities<T extends FaceArrayViewEntity>({
  entities,
  discardSoffitBSP,
  clipContext,
  clippedEntities = [],
}: ClipFacesEntitiesOptions<T>): T[] {
  const { insideBSP } = clipContext;
  let optionsByType: { [type: string]: ClipFaceContext } = {
    [ViewEntityType.FASCIA_TRIM]: {
      clippingNodes: [clippingNodeNew(insideBSP)],
      epsilon: MULTIGABLE_BSP_EPSILON,
    },
    [ViewEntityType.EAVE_FASCIA]: {
      clippingNodes: [clippingNodeNew(insideBSP)],
      epsilon: MULTIGABLE_BSP_EPSILON,
    },
  };

  const defaultClippingNodes = [clippingNodeNew(insideBSP)];
  if (discardSoffitBSP) {
    defaultClippingNodes.push(clippingNodeNew(discardSoffitBSP, true));

    optionsByType = {
      ...optionsByType,
      [ViewEntityType.PROFILE_EXTRUSION]: {
        clippingNodes: [clippingNodeNew(discardSoffitBSP, true)],
        epsilon: MULTIGABLE_BSP_EPSILON,
      },
    };
  }

  const defaultOptions: ClipFaceContext = {
    clippingNodes: defaultClippingNodes,
    checkCoplanarFacing: true,
    epsilon: MULTIGABLE_BSP_EPSILON,
  };

  for (const entity of entities) {
    const options = optionsByType[entity.descriptor.entityType] ?? defaultOptions;
    clipFacesEntity({ entity, options, clippedEntities });
  }

  return clippedEntities;
}

interface ClipFacesEntityOptions<T extends FaceArrayViewEntity> {
  readonly entity: T;
  readonly options: ClipFaceContext;
  readonly clippedEntities?: T[];
}

function clipFacesEntity<T extends FaceArrayViewEntity>({
  entity,
  options,
  clippedEntities = [],
}: ClipFacesEntityOptions<T>): T[] {
  const tag: BuildingGeomTag = { type: "OPERATION", operation: "CLIPPED_ROOF_TRIM" };
  const tempFaces = tagBuildingFaces(
    clipFaces({
      faces: entity.faces,
      options,
    }).filter(faceIsDefined),
    tag,
  );

  if (tempFaces.length > 0) {
    clippedEntities.push({
      ...entity,
      ...faceArrayViewEntityNew(tempFaces, entity.descriptor, entity.material),
    });
  }

  return clippedEntities;
}

function validIntersectingFriezeRail(roofTrimRail: RoofTrimRail, otherRoofTrimRail: RoofTrimRail): boolean {
  if (!isRoofTrimRailTypeFrieze(roofTrimRail) || !isRoofTrimRailTypeFrieze(otherRoofTrimRail)) {
    return false;
  }

  const { rail: srcRail, friezePlane } = roofTrimRail;
  const { rail: otherRail, friezePlane: otherFriezePlane } = otherRoofTrimRail;

  if (!srcRail || !otherRail) {
    return false;
  }

  // we do not consider frieze boards that are not coplanar
  if (!planeIsCoplanar(friezePlane, otherFriezePlane)) {
    return false;
  }

  const srcRail2 = planeProjectPolyLine3(friezePlane, srcRail);
  const otherRail2 = planeProjectPolyLine3(friezePlane, otherRail);

  const res = polyLine2PolyLine2DoIntersect(srcRail2, otherRail2);

  return res;
}

interface BuildFriezeBoardFromRailsOptions {
  readonly subRoof: GeneratedRoof;
  readonly friezeBoardRails: FriezeBoardRail[];
  readonly friezeBoardRTree: RBush3D;
  readonly roofPerimeterRailsRTree: RBush3D;
  readonly profileSource: ActiveProfileSource;
  readonly friezeBoards?: FaceArrayViewEntity[];
  readonly clipContext: MultiGableRoofClipContext;
  readonly maxDepthErrorHandle: MaxBSPDepthErrorHandler;
  readonly issues: BuildingGenerationIssue[];
}

function buildFriezeBoardFromRails({
  subRoof,
  friezeBoardRails,
  friezeBoardRTree,
  roofPerimeterRailsRTree,
  profileSource,
  friezeBoards = [],
  clipContext,
  maxDepthErrorHandle,
  issues,
}: BuildFriezeBoardFromRailsOptions): FaceArrayViewEntity[] {
  const { insideBSP } = clipContext;
  const { roofType } = subRoof;

  for (const friezeBoardRail of friezeBoardRails) {
    const {
      rail,
      roofPerimeterRail,
      startWrapMode,
      endWrapMode,
      friezeBoard,
      friezePlane,
      material: friezeBoardMaterial,
      startClipPlanes,
      endClipPlanes,
    } = friezeBoardRail;

    if (!rail) {
      continue;
    }

    const {
      rail: srcRail,
      edgePlane,
      baseEdge,
      overhang,
      exteriorWallOffsetWithFinish,
      baseRail,
      isStartEdge,
      isEndEdge,
      type,
    } = roofPerimeterRail;

    const isPerimeterStartEndRake = type === RoofPerimeterRailType.RAKE && (isStartEdge || isEndEdge);
    const isShedStartEndRake =
      (roofType === GableRoofType.LEFT_SIDE_SHED_ROOF || roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF) &&
      (isStartEdge || isEndEdge);

    const [nX, nY, nZ] = planeNormal(edgePlane);

    const baseOverhang = overhang - exteriorWallOffsetWithFinish;
    const descriptor = getActiveProfileDescriptor(profileSource, friezeBoard, DEFAULT_TRIM_PROFILE_DESCRIPTOR);
    if (!descriptor) {
      pushMissingProfileReferenceIssue(
        issues,
        profileSource,
        friezeBoard,
        { type: "ROOF", id: subRoof.id },
        "friezeBoard",
      );
      continue;
    }
    const profile = getProfileExtrusionDescriptorProfile(descriptor);
    const friezeBoardProfile = polyLine2SetCW(polyLine2Simplify(polyLine2Copy(profile)));
    const friezeBoardProfileAABB = polyLine2AABB2(friezeBoardProfile);
    const offzetZ = friezeBoardProfileAABB.maxY;
    const friezeBoardSize = aabb2Height(friezeBoardProfileAABB);
    polyLine2Move(friezeBoardProfile, 0, -offzetZ);

    // corner types
    const railStartSlope = polyLine3EdgeSlopeInZ(rail, 0);
    const railEndSlope = -polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1);

    const startCornerInfo = getRoofTrimCornerInfo(friezeBoardRail, friezeBoardRTree, false, false);
    const endCornerInfo = getRoofTrimCornerInfo(friezeBoardRail, friezeBoardRTree, true, false);

    const { type: startTrimCornerType, miterPlane: startMiterPlane } = startCornerInfo;
    const { type: endTrimCornerType, miterPlane: endMiterPlane } = endCornerInfo;

    const { type: startRoofCornerType, otherPerimeterRail: prevPerimeterRail } = getRoofPerimeterCornerInfo(
      roofPerimeterRail,
      roofPerimeterRailsRTree,
      false,
      false,
    );

    const { type: endRoofCornerType, otherPerimeterRail: nextPerimeterRail } = getRoofPerimeterCornerInfo(
      roofPerimeterRail,
      roofPerimeterRailsRTree,
      true,
      false,
    );

    const prevPerimeterRailLength = prevPerimeterRail ? polyLine3Length(prevPerimeterRail.rail) : 0;
    const nextPerimeterRailLength = nextPerimeterRail ? polyLine3Length(nextPerimeterRail.rail) : 0;

    let rail2 = planeProjectPolyLine3(friezePlane, rail);
    const clipPlanes: Plane[] = [];
    const planeGroups: FriezePlaneGroup[] = [];

    const { startPlane: startCollinearPlane, endPlane: endCollinearPlane } = getCollinearRoofTrimMiterPlanes({
      rail,
      plane: friezePlane,
      startCornerInfo,
      endCornerInfo,
    });

    const { startExtension: startCollinearExtension, endExtension: endCollinearExtension } =
      getCollinearRoofTrimExtensions({
        rail,
        roofPerimeterRail,
        startCornerInfo,
        endCornerInfo,
        profileSize: friezeBoardSize,
      });

    // if the frieze board makes a corner
    if (isRoofRailCorner(startTrimCornerType) && startMiterPlane) {
      rail2 = extendRailForCorner(rail2, friezePlane, startMiterPlane, false);
      clipPlanes.push(startMiterPlane);
      // if it has a collinear connection with another frieze board
    } else if (startTrimCornerType === RoofRailCornerType.COPLANAR && startCollinearPlane) {
      polyLine2ExtendStart(rail2, startCollinearExtension);
      clipPlanes.push(startCollinearPlane);
      // no wrap mode is set
    } else if (startWrapMode === WrapFriezeBoardMode.NONE) {
      const [x, y, z] = polyLine3StartPoint(rail);

      if (railStartSlope > DIST_EPSILON && Math.abs(railStartSlope) !== Infinity) {
        const [tX, tY] = polyLine3EdgeTangent(rail, 0, true);
        const [uX, uY] = vec2Normalize(-tY, tX);

        const startClipPlane = planeNew(x, y, z, uX, uY, 0, 0, 0, 1);
        clipPlanes.push(startClipPlane);

        polyLine2ExtendStart(rail2, CORNER_RAIL_EXTENSION);

        const extendStartSrcRail = polyLine3ExtendStart(polyLine3Copy(srcRail), SRC_RAIL_END_EXTENSION);
        const [extendedSrcRailX, extendedSrcRailY, extendedSrcRailZ] = polyLine3StartPoint(extendStartSrcRail);
        //extend the start point of the srcRail by a tolerance to see if the end point is inside the bsp
        const isStartPtInsideBSP = isPointInsideBSP(
          insideBSP,
          extendedSrcRailX,
          extendedSrcRailY,
          extendedSrcRailZ,
          true,
          MULTIGABLE_BSP_EPSILON,
        );

        if (!isStartPtInsideBSP) {
          if (startRoofCornerType === RoofRailCornerType.COPLANAR) {
            const [x0, y0] = polyLine2EdgeEval(baseEdge, 0, 0.5, true);
            planeGroups.push(
              friezePlaneGroupNew(planeNew(x0, y0, 0, nX, nY, nZ, 0, 0, 1), planeReverse(planeNewXY(0, 0, z))),
            );
          } else if (
            !isRoofRailCorner(startRoofCornerType) &&
            !isRoofRailCorner(startTrimCornerType) &&
            !startMiterPlane
          ) {
            const zLimit = getIntersectingRoofTrimRailZLimit(
              friezeBoardRail,
              friezeBoardRTree,
              validIntersectingFriezeRail,
              false,
            );
            if (zLimit !== Number.NEGATIVE_INFINITY) {
              const [x0, y0] = polyLine2EdgeEval(baseEdge, 0, 0.5, true);
              planeGroups.push(
                friezePlaneGroupNew(
                  planeNew(x0, y0, 0, nX, nY, nZ, 0, 0, 1),
                  planeReverse(planeNewXY(0, 0, Math.max(z, zLimit))),
                ),
              );
            } else if (isPerimeterStartEndRake) {
              const [x0, y0] = polyLine2EdgeEval(baseEdge, 0, 0.5, true);

              const [, , baseRailZ] = polyLine3StartPoint(baseRail);
              planeGroups.push(
                friezePlaneGroupNew(
                  planeNew(x0, y0, 0, nX, nY, nZ, 0, 0, 1),
                  planeReverse(planeNewXY(0, 0, baseRailZ)),
                ),
              );
            }
          }
        }
      } else if (
        (!isRoofRailCorner(startRoofCornerType) || prevPerimeterRailLength <= baseOverhang + DIST_EPSILON) &&
        !startMiterPlane &&
        polyLine3EdgeSlopeInZ(srcRail, 0) > DIST_EPSILON
      ) {
        const [x0, y0] = polyLine2EdgeEval(baseEdge, 0, 0.5, true);
        planeGroups.push(
          friezePlaneGroupNew(planeNew(x0, y0, 0, nX, nY, nZ, 0, 0, 1), planeReverse(planeNewXY(0, 0, z))),
        );
      }
    }

    // if the frieze board makes a corner
    if (isRoofRailCorner(endTrimCornerType) && endMiterPlane) {
      rail2 = extendRailForCorner(rail2, friezePlane, endMiterPlane, true);
      clipPlanes.push(endMiterPlane);
      // if it has a collinear connection with another frieze board
    } else if (endTrimCornerType === RoofRailCornerType.COPLANAR && endCollinearPlane) {
      polyLine2ExtendEnd(rail2, endCollinearExtension);
      clipPlanes.push(endCollinearPlane);
      // no wrap mode is set
    } else if (endWrapMode === WrapFriezeBoardMode.NONE) {
      const [x, y, z] = polyLine3EndPoint(rail);

      if (railEndSlope > DIST_EPSILON && Math.abs(railEndSlope) !== Infinity) {
        const [tX, tY] = polyLine3EdgeTangent(rail, polyLine3EdgeCount(rail) - 1);
        const [uX, uY] = vec2Normalize(-tY, tX);

        const endClipPlane = planeNew(x, y, z, uX, uY, 0, 0, 0, 1);
        clipPlanes.push(endClipPlane);

        polyLine2ExtendEnd(rail2, CORNER_RAIL_EXTENSION);

        const extendEndSrcRail = polyLine3ExtendEnd(polyLine3Copy(srcRail), SRC_RAIL_END_EXTENSION);
        const [extendedSrcRailX, extendedSrcRailY, extendedSrcRailZ] = polyLine3EndPoint(extendEndSrcRail);
        //extend the start point of the srcRail by a tolerance to see if the end point is inside the bsp
        const isEndPtInsideBSP = isPointInsideBSP(
          insideBSP,
          extendedSrcRailX,
          extendedSrcRailY,
          extendedSrcRailZ,
          true,
          MULTIGABLE_BSP_EPSILON,
        );

        if (!isEndPtInsideBSP) {
          if (endRoofCornerType === RoofRailCornerType.COPLANAR) {
            const [x0, y0] = polyLine2EdgeEval(baseEdge, 0, 0.5, true);
            planeGroups.push(
              friezePlaneGroupNew(planeNew(x0, y0, 0, -nX, -nY, -nZ, 0, 0, 1), planeReverse(planeNewXY(0, 0, z))),
            );
          } else if (!isRoofRailCorner(endRoofCornerType) && !isRoofRailCorner(endTrimCornerType) && !endMiterPlane) {
            const zLimit = getIntersectingRoofTrimRailZLimit(
              friezeBoardRail,
              friezeBoardRTree,
              validIntersectingFriezeRail,
              true,
            );
            if (zLimit !== Number.NEGATIVE_INFINITY) {
              const [x0, y0] = polyLine2EdgeEval(baseEdge, 0, 0.5, true);
              planeGroups.push(
                friezePlaneGroupNew(
                  planeNew(x0, y0, 0, -nX, -nY, -nZ, 0, 0, 1),
                  planeReverse(planeNewXY(0, 0, Math.max(z, zLimit))),
                ),
              );
            } else if (isPerimeterStartEndRake) {
              const [x0, y0] = polyLine2EdgeEval(baseEdge, 0, 0.5, true);
              const [, , baseRailZ] = polyLine3EndPoint(baseRail);

              planeGroups.push(
                friezePlaneGroupNew(
                  planeNew(x0, y0, 0, -nX, -nY, -nZ, 0, 0, 1),
                  planeReverse(planeNewXY(0, 0, baseRailZ)),
                ),
              );
            }
          }
        }
      } else if (
        (!isRoofRailCorner(endRoofCornerType) || nextPerimeterRailLength <= baseOverhang + DIST_EPSILON) &&
        !endMiterPlane &&
        polyLine3EdgeSlopeInZ(srcRail, polyLine3EdgeCount(srcRail) - 1) < -DIST_EPSILON
      ) {
        const [x0, y0] = polyLine2EdgeEval(baseEdge, 0, 0.5, true);
        planeGroups.push(
          friezePlaneGroupNew(planeNew(x0, y0, 0, -nX, -nY, -nZ, 0, 0, 1), planeReverse(planeNewXY(0, 0, z))),
        );
      }
    }

    if (isShedStartEndRake) {
      const isStartEdgeAndLeftSideShed = roofType === GableRoofType.LEFT_SIDE_SHED_ROOF && isStartEdge;
      const isEndEdgeAndRightSideShed = roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF && isEndEdge;
      const useEndPoint = isEndEdgeAndRightSideShed || isStartEdgeAndLeftSideShed;
      const prevOrNextPerimeterRail = useEndPoint ? nextPerimeterRail : prevPerimeterRail;

      if (!prevOrNextPerimeterRail || (prevOrNextPerimeterRail.type !== type && type === RoofPerimeterRailType.RAKE)) {
        const [x, y, z] = useEndPoint ? polyLine3EndPoint(rail) : polyLine3StartPoint(rail);
        const shedClipPlane = useEndPoint
          ? planeNew(x, y, z, -nX, -nY, -nZ, 0, 0, 1)
          : planeNew(x, y, z, nX, nY, nZ, 0, 0, 1);
        clipPlanes.push(shedClipPlane);
      }
    }

    if (startClipPlanes) {
      planeGroups.push(startClipPlanes);
    }

    if (endClipPlanes) {
      planeGroups.push(endClipPlanes);
    }

    const finalFriezeRail = polyLine3OnPlane(rail2, friezePlane);

    const [tangentX, tangentY, tangentZ] = polyLine3EdgeTangent(finalFriezeRail, 0);
    const [upX, upY, upZ] = vec3Cross(nX, nY, nZ, tangentX, tangentY, tangentZ);
    const brep = extrudeFaceAlongPolyLine3([friezeBoardProfile], finalFriezeRail, upX, upY, upZ);

    let faces = clipFacesWithPlanes({
      faces: brep.faces,
      planes: clipPlanes,
      addCapFaces: true,
      maxDepthErrorHandle,
    });

    for (const { verticalPlane, horizontalPlane } of planeGroups) {
      const node = verticalPlane ? addConvexPlanesToBSPLeaf([planeBSPFromPlane(verticalPlane)]) : null;

      if (!node) {
        continue;
      }

      const frontFaces: Face[] = [];
      const backFaces: Face[] = [];
      const verticalOptions: SplitFaceBSPOptions = {
        node,
        frontFaces,
        backFaces,
        epsilon: MULTIGABLE_BSP_EPSILON,
      };

      splitFacesBSP(faces, verticalOptions);

      const clipFaces: Face[] = horizontalPlane
        ? clipFacesWithPlane({
            faces: frontFaces,
            plane: horizontalPlane,
            addCapFaces: true,
            maxDepthErrorHandle,
          })
        : [];

      faces = [...clipFaces, ...backFaces];
    }

    const tag = { type: "CONDITION", condition: "FRIEZE_BOARD_FROM_RAILS" };

    friezeBoards.push(faceArrayViewEntityNew(tagFaces(faces, tag), descriptor, friezeBoardMaterial));
  }

  return friezeBoards;
}

function extendRailForCorner(rail: PolyLine2, edgePlane: Plane, miterPlane: Plane, isEnd: boolean): PolyLine2 {
  const extension = CORNER_RAIL_EXTENSION;
  const [nX, nY, nZ] = planeNormal(miterPlane);
  const { originX, originY, originZ } = edgePlane;
  const [tx, ty] = planeProject(edgePlane, nX + originX, nY + originY, nZ + originZ);
  const [newTx, newTy] = vec2Normalize(tx, ty);
  const [x0, y0] = isEnd ? polyLine2End(rail) : polyLine2Start(rail);
  const [x1, y1] = vec2Add(x0, y0, newTx * extension, newTy * extension);

  rail = isEnd ? polyLine2Concat(rail, [x1, y1]) : polyLine2Concat([x1, y1], rail);

  return rail;
}

interface BuildFasciaBoardFromRailsOptions {
  readonly subRoof: GeneratedRoof;
  readonly fasciaRails: FasciaRail[] | FasciaTrimRail[];
  readonly fasciaRTree: RBush3D;
  readonly entityType: ViewEntityType;
  readonly isOutsideTrim?: boolean;
  readonly fasciaBoards?: FaceArrayViewEntity[];
  readonly maxDepthErrorHandle: MaxBSPDepthErrorHandler;
}

function buildFasciaBoardFromRails({
  subRoof,
  fasciaRails,
  fasciaRTree,
  entityType,
  isOutsideTrim = false,
  fasciaBoards = [],
  maxDepthErrorHandle,
}: BuildFasciaBoardFromRailsOptions): FaceArrayViewEntity[] {
  const buildPerpendicularMiterPlane = (
    miterPlane: Plane | undefined,
    rail: PolyLine3,
    isEnd: boolean,
  ): Plane | undefined => {
    if (!miterPlane) {
      return undefined;
    }

    const { originX, originY, originZ, vX, vY, vZ } = miterPlane;
    const [tX, tY, tZ] = polyLine3EdgeTangent(rail, isEnd ? polyLine3EdgeCount(rail) - 1 : 0, !isEnd);
    const [uX, uY, uZ] = vec3Cross(vX, vY, vZ, tX, tY, tZ);
    return planeNew(originX, originY, originZ, uX, uY, uZ, vX, vY, vZ);
  };

  const { roofSettings } = subRoof;
  const { flyRafters = true, flyRafterSize = undefined } = roofSettings;

  for (const roofTrimRail of fasciaRails) {
    const { roofPerimeterRail, boardSize, prevBoardSize, nextBoardSize, boardThickness, adjustSize } = roofTrimRail;
    const { eaveType, eaveCutType, rail, edgePlane, rafterDirections, type } = roofPerimeterRail;

    if (
      type === RoofPerimeterRailType.RAKE &&
      eaveType === EaveType.ANGLED_OPEN_OPEN &&
      !flyRafters &&
      ViewEntityType.FASCIA === entityType
    ) {
      continue;
    }

    let fasciaBoardSize = boardSize;

    if (flyRafters && flyRafterSize && type === RoofPerimeterRailType.RAKE && ViewEntityType.FASCIA === entityType) {
      fasciaBoardSize = flyRafterSize;
    }

    if (!rail) {
      continue;
    }

    const eaveReturnPlaneGroups = buildEaveReturnPlaneGroups(roofTrimRail, fasciaRTree);

    const startRafterDir = getRoofPerimeterRailStartRafterDir(roofPerimeterRail);

    const hasSquareCutEave: boolean = hasEaveSquaredCut(eaveType, eaveCutType);

    // build the plane for the fascia top trim
    let plane = edgePlane;
    if (hasSquareCutEave && isEaveRoofPerimeterRail(roofPerimeterRail)) {
      const { uX, uY, uZ } = edgePlane;
      const [vX, vY, vZ] = vec3Cross(startRafterDir[0], startRafterDir[1], startRafterDir[2], uX, uY, uZ);

      plane = planeNew(rail[0], rail[1], rail[2], uX, uY, uZ, vX, vY, vZ);
    }

    // get the rail slopes
    const railStartSlope = Math.abs(polyLine3EdgeSlopeInZ(rail, 0));
    const railEndSlope = Math.abs(polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1));

    // corner types
    const startCornerInfo = getRoofTrimCornerInfo(roofTrimRail, fasciaRTree, false, true);
    const endCornerInfo = getRoofTrimCornerInfo(roofTrimRail, fasciaRTree, true, true);

    const { type: startCornerType } = startCornerInfo;
    const { type: endCornerType } = endCornerInfo;

    let { miterPlane: startMiterPlane } = startCornerInfo;
    let { miterPlane: endMiterPlane } = endCornerInfo;

    // if the fascia board changes in size, we need to adjust the miter planes,
    // to avoid diagonal cuts
    if (Math.abs(fasciaBoardSize - prevBoardSize) > DIST_EPSILON) {
      startMiterPlane = buildPerpendicularMiterPlane(startMiterPlane, rail, false);
    }

    if (Math.abs(fasciaBoardSize - nextBoardSize) > DIST_EPSILON) {
      endMiterPlane = buildPerpendicularMiterPlane(endMiterPlane, rail, true);
    }

    const startClipPlane = isRoofRailCorner(startCornerType) ? startMiterPlane : undefined;
    const endClipPlane = isRoofRailCorner(endCornerType) ? endMiterPlane : undefined;

    // build the fascia top rail
    const topRailNoExtension = planeProjectPolyLine3(plane, rail);
    let topRail = polyLine2Copy(topRailNoExtension);
    if (isRoofRailCorner(startCornerType) && startMiterPlane) {
      topRail = extendRailForCorner(topRail, plane, startMiterPlane, false);
    }

    if (isRoofRailCorner(endCornerType) && endMiterPlane) {
      topRail = extendRailForCorner(topRail, plane, endMiterPlane, true);
    }

    // build the fascia bottom
    let [dirX, dirY, dirZ] = planeNormal(plane);
    let finalBoardThickness = boardThickness;
    const [rafterDirX, rafterDirY, rafterDirZ] = rafterDirections[0];
    let bottomOffset = fasciaBoardSize;

    if (eaveCutType === EaveCutType.PLUMB && rafterDirZ > DIST_EPSILON && adjustSize) {
      const [prjDirX, prjDirY, proDirZ] = vec3Normalize(rafterDirX, rafterDirY, 0);

      const angle = vec3AngleBetween(rafterDirX, rafterDirY, rafterDirZ, prjDirX, prjDirY, proDirZ);

      bottomOffset = fasciaBoardSize / Math.cos(angle);

      dirX = rafterDirX;
      dirY = rafterDirY;
      dirZ = rafterDirZ;

      finalBoardThickness = boardThickness / Math.cos(angle);
    }

    const bottomRail = offsetPolyLine2(topRail, bottomOffset);

    // adjust the start and end point of the bottom rail
    if (startCornerType !== RoofRailCornerType.COPLANAR) {
      bottomRail[0] = topRail[0];
      bottomRail[1] = topRail[1] - fasciaBoardSize / Math.cos(Math.atan(railStartSlope));
    }

    if (endCornerType !== RoofRailCornerType.COPLANAR) {
      bottomRail[bottomRail.length - 2] = topRail[topRail.length - 2];
      bottomRail[bottomRail.length - 1] =
        topRail[topRail.length - 1] - fasciaBoardSize / Math.cos(Math.atan(railEndSlope));
    }

    // bottom extensions
    const { startExtension: startBottomExtension, endExtension: endBottomExtension } = getCollinearRoofTrimExtensions({
      rail,
      roofPerimeterRail,
      startCornerInfo,
      endCornerInfo,
      profileSize: fasciaBoardSize,
      filter: soffitCollinearExtensionFilter,
    });

    polyLine2ExtendStart(bottomRail, startBottomExtension);
    polyLine2ExtendEnd(bottomRail, endBottomExtension);

    // create the board
    buildFasciaBoard({
      roofPerimeterRail,
      topRailNoExtension: isOutsideTrim && hasSquareCutEave ? topRail : topRailNoExtension,
      topRail,
      bottomRail,
      plane,
      boardSize: fasciaBoardSize,
      prevBoardSize,
      nextBoardSize,
      boardThickness: isOutsideTrim ? -finalBoardThickness : finalBoardThickness,
      dirX,
      dirY,
      dirZ,
      entityType,
      startClipPlane,
      endClipPlane,
      eaveReturnPlaneGroups,
      fasciaBoards,
      subRoof,
      maxDepthErrorHandle,
    });
  }

  return fasciaBoards;
}

interface BuildRoofPerimetersTrimsOptions {
  readonly roofSystem: GeneratedRoofSystem;
  readonly walls: Table<WallSystem>;
  readonly porches: Table<Porch>;
  readonly wallSettings: WallSettings;
  readonly profileSource: ActiveProfileSource;
  readonly propSettings: PropAttributeTable;
  readonly mergedPropLibrary: MergedPropLibrary;
  readonly wallOverlapForFriezeBoardBSPs: NodeBSP[];
  readonly gableWallOverlapBSPs: NodeBSP[];
  readonly halfStoryBSPsTable: Table<NodeBSP[]>;
  readonly issues: BuildingGenerationIssue[];
}

export async function buildRoofPerimetersTrims({
  roofSystem,
  walls,
  porches,
  wallSettings,
  profileSource,
  propSettings,
  mergedPropLibrary,
  wallOverlapForFriezeBoardBSPs,
  gableWallOverlapBSPs,
  halfStoryBSPsTable,
  issues,
}: BuildRoofPerimetersTrimsOptions): Promise<GeneratedRoofSystem> {
  const { roofs: subRoofs, clipContext } = roofSystem;
  const { overlapBSP } = clipContext;
  const { roofPerimeterRailsTable, rtree } = prepareRoofPerimeterRails({
    subRoofs,
    clipContext,
    collectOverhangClipping,
  });

  // build eave returns RTree
  const gableReturnsRTreeMap: RWTable<RBush<AABB2>> = {};
  const eaveReturnRTree = new RBush3D();
  for (const subRoofId in subRoofs) {
    const subRoof = subRoofs[subRoofId];
    const newRoofPerimeterRails = roofPerimeterRailsTable[subRoofId] ?? [];

    gableReturnsRTreeMap[subRoofId] = buildEaveReturnsRTree({
      subRoof,
      subRoofs,
      roofPerimeterRails: newRoofPerimeterRails,
      roofPerimeterRailsRTree: rtree,
      eaveReturnRTree,
      wallSettings,
      overlapBSP,
    });
  }

  // build trim rails (computed once for all sub-roofs since inputs are loop-invariant)
  const { trimRailsTable: exposedRaftersRailsTable, rtree: exposedRaftersRTree } =
    buildExposedRaftersRails(roofPerimeterRailsTable);
  const { trimRailsTable: soffitRailsTable, rtree: soffitRTree } = buildSoffitRails(roofPerimeterRailsTable);
  const { trimRailsTable: fasciaRailsTable, rtree: fasciaRTree } = buildFasciaRails(roofPerimeterRailsTable);
  const { trimRailsTable: fasciaTopTrimRailsTable, rtree: fasciaTopTrimRTree } =
    buildFasciaTopTrimRails(roofPerimeterRailsTable);
  const { trimRailsTable: friezeBoardRailsTable, rtree: friezeBoardRTree } = buildFriezeBoardRails({
    subRoofs,
    walls,
    roofRailsTable: roofPerimeterRailsTable,
    clipContext,
    profileSource,
    porches,
    rtree,
    wallOverlapForFriezeBoardBSPs,
    gableWallOverlapBSPs,
    halfStoryBSPsTable,
    gableReturnsRTreeMap,
    overlapBSP,
    issues,
  });

  // build the trims for all sub roofs
  const newSubRoofs: RWTable<GeneratedRoof> = {};
  const newSubRoofExposedRafters: RWTable<FaceArrayViewEntity[]> = {};
  const newSubRoofFriezeBoards: RWTable<FaceArrayViewEntity[]> = {};
  const newSubRoofFasciaBoards: RWTable<FaceArrayViewEntity[]> = {};
  const newSubRoofFasciaTopTrims: RWTable<FaceArrayViewEntity[]> = {};
  const newSubRoofSoffits: RWTable<GeneratedRoofSoffit[]> = {};

  for (const subRoofId in subRoofs) {
    const subRoof = subRoofs[subRoofId];
    const newRoofPerimeterRails = roofPerimeterRailsTable[subRoofId] ?? [];

    const maxDepthErrorHandle = getMaxDepthErrorHandle(issues, { type: "ROOF", id: subRoofId });

    const {
      roofSettings,
      props,
      roofVolume,
      trimsRoofMat,
      roofTopFaces,
      roofTopPlates,
      roofingMaterialFaces,
      extrudedTrimsRoofMat,
      roofPerimeterRails,
    } = subRoof;

    const { exactEdges } = roofVolume;

    const roofingMaterialThickness = getRoofingMaterialThickness(roofSettings);
    const roofingMaterialExtension = getRoofingMaterialExtension(roofSettings);

    const exposedRafters: FaceArrayViewEntity[] = [];
    const fasciaBoards: FaceArrayViewEntity[] = [];
    const fasciaTopTrims: FaceArrayViewEntity[] = [];
    const friezeBoards: FaceArrayViewEntity[] = [];
    const soffits: GeneratedRoofSoffit[] = [];
    const gableVentFaces: FaceArrayViewEntity[] = [];

    let newRoofTopFaces: RoofFace[] = [];
    const newRoofProps: Prop[] = [];
    const newTrimsRoofMat: FaceArrayViewEntity[] = [];

    // build eave returns
    await buildEaveReturns({
      subRoof,
      subRoofs,
      clipContext,
      roofPerimeterRails: newRoofPerimeterRails,
      eaveReturnRTree,
      fasciaBoards,
      fasciaTopTrims,
      soffits,
      friezeBoards,
      gableVentFaces,
      roofTopFaces: newTrimsRoofMat,
      props: newRoofProps,
      walls,
      wallOverlapForFriezeBoardBSPs,
      wallSettings,
      profileSource,
      propSettings,
      mergedPropLibrary,
      issues,
    });

    /**
     * Build the exposed rafters only if generateRoofFraming is set to false
     * @TODO remove this block of code when new roof framing is completely implemented
     */
    buildExposedRaftersFromRails({
      subRoof,
      exposedRaftersRails: exposedRaftersRailsTable[subRoofId] ?? [],
      exposedRaftersRTree,
      exposedRafters,
      extensionRoofTopFaces: newRoofTopFaces,
      maxDepthErrorHandle,
    });

    // build the soffit
    buildSoffitFromRails(
      subRoof,
      soffitRailsTable[subRoofId] ?? [],
      soffitRTree,
      soffits,
      maxDepthErrorHandle,
      newRoofTopFaces,
    );

    // build the fascia
    buildFasciaBoardFromRails({
      subRoof,
      fasciaRails: fasciaRailsTable[subRoofId] ?? [],
      fasciaRTree,
      entityType: ViewEntityType.FASCIA,
      fasciaBoards,
      maxDepthErrorHandle,
    });

    // build the fascia top trim
    buildFasciaBoardFromRails({
      subRoof,
      fasciaRails: fasciaTopTrimRailsTable[subRoofId] ?? [],
      fasciaRTree: fasciaTopTrimRTree,
      entityType: ViewEntityType.FASCIA_TRIM,
      fasciaBoards: fasciaTopTrims,
      isOutsideTrim: true,
      maxDepthErrorHandle,
    });

    // build the frieze boards
    buildFriezeBoardFromRails({
      subRoof,
      friezeBoardRails: friezeBoardRailsTable[subRoofId] ?? [],
      friezeBoardRTree,
      roofPerimeterRailsRTree: rtree,
      profileSource,
      friezeBoards,
      clipContext,
      maxDepthErrorHandle,
      issues,
    });

    // merge coplanar faces
    newRoofTopFaces = mergeCoplanarRoofTopFaces(newRoofTopFaces);

    // build the new roof top plates
    const newRoofTopPlates = extrudeRoofTopFaces(newRoofTopFaces, roofPerimeterRails);

    // build the new roofing material faces
    const newRoofingMaterialFaces = extrudeRoofingMaterialFaces(
      newRoofTopFaces,
      roofingMaterialThickness,
      roofingMaterialExtension,
      exactEdges,
      maxDepthErrorHandle,
    );

    const newExtrudedTrimsRoofMat = extrudeRoofingMaterialFaceEntities(
      newTrimsRoofMat,
      roofingMaterialThickness,
      roofingMaterialExtension,
      maxDepthErrorHandle,
    );

    // store the new trims
    newSubRoofExposedRafters[subRoofId] = exposedRafters;
    newSubRoofFriezeBoards[subRoofId] = friezeBoards;
    newSubRoofFasciaBoards[subRoofId] = fasciaBoards;
    newSubRoofFasciaTopTrims[subRoofId] = fasciaTopTrims;
    newSubRoofSoffits[subRoofId] = soffits;

    // create the new sub roof by adding the new trims
    newSubRoofs[subRoofId] = {
      ...subRoof,
      soffitRails: { [subRoofId]: soffitRailsTable[subRoofId] ?? [] },
      roofPerimeterRails: newRoofPerimeterRails,
      props: [...props, ...newRoofProps],
      roofTopFaces: [...roofTopFaces, ...newRoofTopFaces],
      trimsRoofMat: [...trimsRoofMat, ...newTrimsRoofMat],
      roofTopPlates: [...roofTopPlates, ...newRoofTopPlates],
      roofingMaterialFaces: [...roofingMaterialFaces, ...newRoofingMaterialFaces],
      extrudedTrimsRoofMat: [...extrudedTrimsRoofMat, ...newExtrudedTrimsRoofMat],
      friezeBoardRails: friezeBoardRailsTable[subRoofId] ?? [],
      fasciaRails: fasciaRailsTable[subRoofId] ?? [],
      fasciaTopTrimRails: fasciaTopTrimRailsTable[subRoofId] ?? [],
      gableVentFaces,
    };
  }

  // clip the new trims
  const discardSoffitBSP = buildSoffitDiscardBSP(clipContext, newSubRoofs);

  for (const subRoofId in newSubRoofs) {
    const subRoof = newSubRoofs[subRoofId];

    newSubRoofs[subRoofId] = clipAndMergeRoofTrims({
      subRoof,
      exposedRafters: newSubRoofExposedRafters[subRoofId],
      fasciaBoards: newSubRoofFasciaBoards[subRoofId],
      fasciaTopTrims: newSubRoofFasciaTopTrims[subRoofId],
      friezeBoards: newSubRoofFriezeBoards[subRoofId],
      soffits: newSubRoofSoffits[subRoofId],
      clipContext,
      discardSoffitBSP,
    });
  }

  return {
    ...roofSystem,
    roofs: newSubRoofs,
  };
}

function tagTrimFaceArrays<T extends FaceArrayViewEntity>(trimFaceArrays: T[], tags: BuildingGeomTag[]): T[] {
  const tagFaceArray = (faceArray: T, tags: BuildingGeomTag[]): T => {
    const tagFaces = tagBuildingFaces(
      faceArray.faces.filter((face) => face !== null),
      tags,
    );

    return {
      ...faceArray,
      faces: tagFaces,
    };
  };

  return trimFaceArrays.map((x) => tagFaceArray(x, tags));
}
